import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { UpdateFamilyRequest } from '../types';
import { FAMILY_ENDPOINTS } from '../config/api.config';
import {
  HomeIcon,
  PencilSquareIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const currencies = [
  { code: 'USD', name: 'US Dollar ($)' },
  { code: 'EUR', name: 'Euro (€)' },
  { code: 'GBP', name: 'British Pound (£)' },
  { code: 'JPY', name: 'Japanese Yen (¥)' },
  { code: 'CAD', name: 'Canadian Dollar (C$)' },
  { code: 'AUD', name: 'Australian Dollar (A$)' },
  { code: 'CHF', name: 'Swiss Franc (CHF)' },
  { code: 'CNY', name: 'Chinese Yuan (¥)' },
  { code: 'INR', name: 'Indian Rupee (₹)' },
  { code: 'BRL', name: 'Brazilian Real (R$)' },
];

const FamilySettings: React.FC = () => {
  const { currentFamily, currentUser, isAdmin } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState<UpdateFamilyRequest>({
    name: '',
    address: '',
    currency: 'USD',
  });

  useEffect(() => {
    if (currentFamily) {
      setFormData({
        name: currentFamily.name,
        address: currentFamily.address || '',
        currency: currentFamily.currency,
      });
    }
  }, [currentFamily]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isAdmin) {
      setError('Only admin users can update family settings');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare the update request with only changed fields
      const updateRequest: any = {};

      if (formData.name !== currentFamily?.name) {
        updateRequest.name = formData.name;
      }

      if (formData.address !== (currentFamily?.address || '')) {
        updateRequest.address = formData.address || null;
      }

      if (formData.currency !== currentFamily?.currency) {
        updateRequest.currency = formData.currency;
      }

      const response = await fetch(FAMILY_ENDPOINTS.UPDATE(currentFamily?.id || ''), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(updateRequest),
      });

      if (response.ok) {
        const updatedFamily = await response.json();
        // Update family in localStorage
        localStorage.setItem('family', JSON.stringify(updatedFamily));
        setSuccess('Family settings updated successfully!');
        setIsEditing(false);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to update family settings');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (currentFamily) {
      setFormData({
        name: currentFamily.name,
        address: currentFamily.address || '',
        currency: currentFamily.currency,
      });
    }
    setIsEditing(false);
    setError(null);
    setSuccess(null);
  };

  if (!currentFamily) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <div className="w-16 h-16 mb-4 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center">
          <HomeIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
        </div>
        <p className="text-gray-500 dark:text-gray-400 font-medium">No family information available</p>
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6"
    >
      {/* Header */}
      <motion.div
        variants={fadeInUp}
        className="backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-lg"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center shadow-lg shadow-violet-500/30">
              <HomeIcon className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Family Settings</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Manage your family preferences
              </p>
            </div>
          </div>
          {isAdmin && !isEditing && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-violet-500 to-purple-500 text-white font-semibold rounded-xl shadow-lg shadow-violet-500/30 hover:shadow-xl transition-all"
            >
              <PencilSquareIcon className="h-5 w-5" />
              Edit Settings
            </motion.button>
          )}
        </div>
      </motion.div>

      {/* Success/Error Messages */}
      {success && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="backdrop-blur-xl bg-emerald-50/80 dark:bg-emerald-900/20 border border-emerald-200/50 dark:border-emerald-700/50 rounded-2xl p-4 flex items-center gap-3"
        >
          <CheckCircleIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          <span className="text-emerald-700 dark:text-emerald-300">{success}</span>
        </motion.div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="backdrop-blur-xl bg-red-50/80 dark:bg-red-900/20 border border-red-200/50 dark:border-red-700/50 rounded-2xl p-4 flex items-center gap-3"
        >
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
          <span className="text-red-700 dark:text-red-300">{error}</span>
        </motion.div>
      )}

      {/* Family Information */}
      <motion.div
        variants={fadeInUp}
        className="backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-lg"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
            <HomeIcon className="h-5 w-5 text-white" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Family Information</h2>
        </div>

        {isEditing ? (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Family Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
                placeholder="Enter family name"
              />
            </div>

            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Address (Optional)
              </label>
              <textarea
                id="address"
                name="address"
                rows={3}
                value={formData.address}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
                placeholder="Enter family address"
              />
            </div>

            <div>
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Preferred Currency *
              </label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleInputChange}
                className="w-full px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
              >
                {currencies.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3 pt-4">
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleCancel}
                className="flex-1 px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
              >
                Cancel
              </motion.button>
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 text-white font-medium shadow-lg shadow-violet-500/30 hover:shadow-xl transition-all disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </motion.button>
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
                <HomeIcon className="h-4 w-4" />
                Family Name
              </div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{currentFamily.name}</p>
            </div>

            <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
                <MapPinIcon className="h-4 w-4" />
                Address
              </div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {currentFamily.address || <span className="text-gray-400 dark:text-gray-500 italic">Not provided</span>}
              </p>
            </div>

            <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
                <CurrencyDollarIcon className="h-4 w-4" />
                Currency
              </div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {currencies.find(c => c.code === currentFamily.currency)?.name || currentFamily.currency}
              </p>
            </div>

            <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
                <CalendarDaysIcon className="h-4 w-4" />
                Created
              </div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {new Date(currentFamily.created_at).toLocaleDateString()}
              </p>
            </div>

            {!isAdmin && (
              <div className="md:col-span-2 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200/50 dark:border-amber-700/50 flex items-center gap-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                <span className="text-amber-700 dark:text-amber-300 text-sm">
                  Only admin users can modify family settings. Contact your family admin to make changes.
                </span>
              </div>
            )}
          </div>
        )}
      </motion.div>

      {/* Family Members Summary */}
      <motion.div
        variants={fadeInUp}
        className="backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-lg"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
            <UserGroupIcon className="h-5 w-5 text-white" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Family Members</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50">
            <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Logged in as</div>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">{currentUser?.name}</p>
          </div>
          <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/50">
            <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Role</div>
            <div className="flex items-center gap-2">
              <ShieldCheckIcon className={`h-5 w-5 ${isAdmin ? 'text-violet-500' : 'text-gray-400'}`} />
              <p className="text-lg font-semibold capitalize text-gray-900 dark:text-white">{currentUser?.role}</p>
            </div>
          </div>
        </div>
        {isAdmin && (
          <p className="mt-4 text-sm text-violet-600 dark:text-violet-400">
            Visit the <strong>Users</strong> page to manage family members.
          </p>
        )}
      </motion.div>
    </motion.div>
  );
};

export default FamilySettings;
